import api from "@/lib/api";
import type { TrainingCenter, TrainingClass, TrainingEnrollment, TrainingSession } from "@/types/training";
import { paymentService, type PaymentCreateResponse } from "@/services/payment.service";
import { getChargedAmount } from "@/utils/payment.utils";

export const trainingCenterService = {
  async getOrCreate(teamId: string): Promise<TrainingCenter> {
    const { data } = await api.get(`/teams/${teamId}/training/center`);
    return data;
  },

  async upsert(teamId: string, payload: Partial<TrainingCenter>): Promise<TrainingCenter> {
    const { data } = await api.patch(`/teams/${teamId}/training/center`, payload);
    return data;
  },

  async getPublic(teamId: string): Promise<TrainingCenter> {
    const { data } = await api.get(`/teams/${teamId}/training/center`);
    return data;
  },
};

export const trainingSessionService = {
  async list(teamId: string): Promise<TrainingSession[]> {
    const { data } = await api.get(`/teams/${teamId}/training/sessions`);
    return data;
  },

  async get(teamId: string, sessionId: string): Promise<TrainingSession> {
    const { data } = await api.get(`/teams/${teamId}/training/sessions/${sessionId}`);
    return data;
  },

  async getPublic(sessionId: string): Promise<TrainingSession> {
    const { data } = await api.get(`/training/sessions/${sessionId}`);
    return data;
  },

  async create(teamId: string, payload: Record<string, unknown>): Promise<TrainingSession> {
    const { data } = await api.post(`/teams/${teamId}/training/sessions`, payload);
    return data;
  },

  async update(
    teamId: string,
    sessionId: string,
    payload: Record<string, unknown>,
  ): Promise<TrainingSession> {
    const { data } = await api.patch(`/teams/${teamId}/training/sessions/${sessionId}`, payload);
    return data;
  },

  async cancel(teamId: string, sessionId: string): Promise<TrainingSession> {
    const { data } = await api.post(`/teams/${teamId}/training/sessions/${sessionId}/cancel`);
    return data;
  },

  async listEnrollments(teamId: string, sessionId: string): Promise<TrainingEnrollment[]> {
    const { data } = await api.get(`/teams/${teamId}/training/sessions/${sessionId}/enrollments`);
    return data;
  },

  async checkIn(teamId: string, sessionId: string, verificationCode: string) {
    const { data } = await api.post(`/teams/${teamId}/training/sessions/${sessionId}/check-in`, {
      verificationCode,
    });
    return data;
  },

  async resolveByShortCode(shortCode: string): Promise<{ id: string; shortCode: string }> {
    const { data } = await api.get(`/training/by-short/${shortCode}`);
    return data;
  },

  async listPublic(params?: { sport?: string; from?: string; to?: string }): Promise<TrainingSession[]> {
    const { data } = await api.get("/training/sessions/public", { params });
    return data;
  },
};

export const trainingClassService = {
  async list(teamId: string): Promise<TrainingClass[]> {
    const { data } = await api.get(`/teams/${teamId}/training/classes`);
    return data;
  },

  async get(teamId: string, scheduleId: string): Promise<TrainingClass> {
    const { data } = await api.get(`/teams/${teamId}/training/classes/${scheduleId}`);
    return data;
  },

  async listPublic(teamId: string): Promise<TrainingClass[]> {
    const { data } = await api.get(`/teams/${teamId}/training/classes/catalog/public`);
    return data;
  },

  async listPublicAll(params?: { sport?: string }): Promise<TrainingClass[]> {
    const { data } = await api.get("/training/classes/public", { params });
    return data;
  },

  async getPublic(teamId: string, classId: string): Promise<TrainingClass> {
    const { data } = await api.get(`/teams/${teamId}/training/classes/${classId}/public`);
    return data;
  },

  async create(teamId: string, payload: Record<string, unknown>): Promise<TrainingClass> {
    const { data } = await api.post(`/teams/${teamId}/training/classes`, payload);
    return data;
  },

  async update(
    teamId: string,
    classId: string,
    payload: Record<string, unknown>,
  ): Promise<TrainingClass> {
    const { data } = await api.patch(`/teams/${teamId}/training/classes/${classId}`, payload);
    return data;
  },

  async enroll(teamId: string, classId: string, paymentMethod?: string): Promise<TrainingEnrollment> {
    const { data } = await api.post(`/teams/${teamId}/training/classes/${classId}/enroll`, {
      paymentMethod,
    });
    return data;
  },

  async listEnrollments(teamId: string, scheduleId: string): Promise<TrainingEnrollment[]> {
    const { data } = await api.get(`/teams/${teamId}/training/classes/${scheduleId}/enrollments`);
    return data;
  },
};

export const trainingEnrollmentService = {
  async enroll(sessionId: string, paymentMethod?: string): Promise<TrainingEnrollment> {
    const { data } = await api.post(`/training/sessions/${sessionId}/enroll`, { paymentMethod });
    return data;
  },

  async myEnrollments(): Promise<TrainingEnrollment[]> {
    const { data } = await api.get("/me/training/enrollments");
    return data;
  },

  async myEventEnrollments(): Promise<TrainingEnrollment[]> {
    const { data } = await api.get("/me/training/events/enrollments");
    return data;
  },

  async myClassEnrollments(): Promise<TrainingEnrollment[]> {
    const { data } = await api.get("/me/training/classes/enrollments");
    return data;
  },

  async cancel(enrollmentId: string): Promise<void> {
    await api.delete(`/me/training/enrollments/${enrollmentId}`);
  },

  async verifyPayment(code: string): Promise<TrainingEnrollment> {
    const { data } = await api.get(`/training/verify-payment/${code}`);
    return data;
  },

  async createPixPayment(
    enrollment: TrainingEnrollment,
    payer: { name: string; email: string },
  ): Promise<PaymentCreateResponse> {
    const session = enrollment.session;
    const trainingClass = enrollment.trainingClass;
    const base = Number(enrollment.price);
    const passFeesToUser = session?.passFeesToUser ?? true;
    const label = trainingClass?.name ?? session?.title ?? "Inscrição";
    return paymentService.createPayment({
      transaction_amount: getChargedAmount(base, "PIX", passFeesToUser),
      payment_method_id: "pix",
      description: trainingClass ? `CT: ${label}` : `Evento: ${label}`,
      payer,
      trainingSessionId: enrollment.sessionId,
      trainingEnrollmentId: enrollment.id,
      metadata: {
        type: "training_payment",
        trainingSessionId: enrollment.sessionId,
        trainingClassId: enrollment.classId,
        trainingEnrollmentId: enrollment.id,
      },
    });
  },
};
